// index.tsx
import * as React from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { PageProps } from '../../types/navigationProperties';
import { PaginationRequest, PaginatedResponse } from '../../types/pagination';
import { getAllAbouts, Presentation } from '../../services/PresentationsAboutServiceProvider';
import { PresentationLink } from '../../types/presentationLink';
import { displayErrorToast } from '../../utils/toasts';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader';
import Footer from '../../components/Footer';
import Lightbox from "yet-another-react-lightbox";
import Inline from "yet-another-react-lightbox/plugins/inline";
import { VideoLightShot } from '../../components/VideoLightShot';
import { GalleryImage } from '../../types/galleryImage';
import LoaderSpinner from '../../components/LoaderSpinner';
import { LightShotSingle } from '../../components/LightShotSingle';
import aboutPage from '../../assets/img/about_page.jpg';
import { SetButtonCallbackFunction } from '../../types/callback';
import { SEO } from '../../components/SEO';
import { MetaTag } from '../../services/SEOServiceProvider';
import { ColorfulText } from '../../components/ColorfulText';


export default function AboutUsDetails({ props, setPageName, metaTags }: { props: PageProps, setPageName: SetButtonCallbackFunction, metaTags: MetaTag[] }) {

    const language = props.language;
    const { t } = useTranslation();
    const [data, setData] = React.useState<PaginatedResponse<Presentation>>();
    const [selectedVideos, setSelectedVideos] = React.useState<PresentationLink[]>();
    const [open, setOpen] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(true);
    const fadeDelay: number = 300;
    const swipeDelay: number = 300;
    const zoomDelay: number = 300;
    const [openImage, setOpenImage] = React.useState(false);
    const [selectedImage, setSelectedImage] = React.useState<GalleryImage>();
    const [metaTag, setMetaTag] = React.useState<MetaTag>();

    React.useEffect(() => {
        setPageName('about-us');
        let metaTagData = metaTags !== undefined && metaTags.filter((item: MetaTag) => item.page === 'about-us' );
        if(metaTagData && metaTagData.length > 0) {
            let aboutPageMetaTag: MetaTag = {...metaTagData[0]} as MetaTag;
            aboutPageMetaTag.url = window.location.href;
            aboutPageMetaTag.image = aboutPage;
            setMetaTag(aboutPageMetaTag);
        }

        const pagination: PaginationRequest = { page: 0, page_size: 5};
        (async () => {
            try {
                setLoading(true);
                const aboutUsData = await getAllAbouts(pagination);
                console.log(aboutUsData);
                setData(aboutUsData);
            } catch(error) {
                displayErrorToast(error);
            } finally {
                setLoading(false);
            }
        })();
    }, [language, setPageName, metaTags]);

    const headingProps = {
        title: t("about.title"),
        description: undefined
    };

    const handleOpenLightShotVideo = (items: PresentationLink[]) => {
        setSelectedVideos(items);
        setOpen(true);
    }

    const getVideoPreviewSlides = (items: PresentationLink[]) => {
        let slides = items.map((item: PresentationLink) => { let slide: GalleryImage = { src: item.thumbnail, name: item.thumbnail }; return slide; });
        if(slides.length > 0) {
            slides.map((item: GalleryImage) => {
                if(item.src !== null) {
                    const videoId = item.src.match(/(?:embed\/|v=)([\w-]{11})/);
                    if(videoId !== null) {
                        item.src = `https://img.youtube.com/vi/` + videoId[1] + `/maxresdefault.jpg`
                    }
                }
            });
        }
        return slides;
    }

    const handleOpenLightShot = (image: string, name: string) => {
        let item: GalleryImage = { src: image, name: name };
        setSelectedImage(item);
        setOpenImage(true);
    }

    return loading ? (
        <div>
            <SEO metaTag={metaTag} />
            <LoaderSpinner loading={loading} />
        </div>
    ) : (
        <Box>
            <SEO metaTag={metaTag} />
            <LightShotSingle image={selectedImage} opened={openImage} setOpenLightShot={setOpenImage} />
            <VideoLightShot videos={selectedVideos} opened={open} setOpenLightShot={setOpen} />
            <Box>
                <PageHeader props={headingProps} />
            </Box>
            <Box sx={{ py: '40px', px: '20px', paddingTop: { xs: '0px', sm: '0px', md: '40px' }, bgcolor: 'primary.light' }}
                 style={{ width: '100%', height: '100%' }}>
                <Container>
                    <Box sx={{ marginTop: { sm: '8px', md: '9px', lg: '11px' } }}>
                        <ColorfulText style={{
                                textAlign: 'center',
                                fontSize: { sm: '14px', md: '16px', lg: '18px' } }}
                                      text={t("home.heading.1")}
                                      randomChars={true} skipChar={[]} useColor={'random'} />
                    </Box>
                    <Box sx={{ my: { sm: '8px', md: '9px', lg: '11px' } }}>
                        <ColorfulText style={{
                                textAlign: 'center',
                                fontSize: { sm: '14px', md: '16px', lg: '18px' } }}
                                      text={t("home.heading.2")}
                                      randomChars={true} skipChar={[]} useColor={'random'} />
                    </Box>
                </Container>
                <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
                    <Grid size={{ xs: 12, sm: 12, md: 6 }} sx={{
                                    display: 'flex',
                                    position: 'relative',
                                    height: '100%'
                                }}>
                        {data !== undefined  && data.results.map((about, index) => (
                            <Box key={index} sx={{ paddingTop: { xs: '40px', sm: '40px', md: '0px' }, width: '100%' }}>
                                <Box>
                                    <Typography sx={{
                                            fontSize: { sm: '14px', md: '16px', lg: '18px' },
                                            textTransform: 'uppercase',
                                            paddingBottom: '16px'
                                         }}>{about.title.value}
                                    </Typography>
                                    <Typography sx={{ whiteSpace: "pre-wrap", paddingBottom: '16px' }}>{ about.description.value }</Typography>
                                </Box>
                                <Box sx={{ cursor: 'pointer' }}
                                     onClick={((event) => handleOpenLightShotVideo(about.presentation_video_links !== undefined ? about.presentation_video_links : []))}>
                                    {about.presentation_video_links !== undefined ? (
                                        <Lightbox slides={getVideoPreviewSlides(about.presentation_video_links)}
                                                  plugins={[Inline]}
                                                  on={{
                                                    click: () => setOpen(false),
                                                  }}
                                                  animation={{
                                                    fade: fadeDelay,
                                                    swipe: swipeDelay,
                                                    zoom: zoomDelay
                                                  }}
                                                  carousel={{
                                                    padding: 0,
                                                    spacing: 0,
                                                    imageFit: "cover",
                                                  }}
                                                  inline={{
                                                    style: {
                                                        width: "100%",
                                                        maxHeight: '400px',
                                                        aspectRatio: "3 / 2",
                                                        margin: "0 auto",
                                                    },
                                                  }} />
                                    ) : (<></>)}
                                </Box>
                            </Box>
                        ))}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 6 }} sx={{ display: 'flex' }}>
                        <img loading="lazy" src={aboutPage}
                            style={{
                                position: 'relative',
                                margin: 'auto',
                                maxWidth: '500px',
                                width: '100%',
                            }}
                            alt="about page" onClick={() => handleOpenLightShot(aboutPage, "about page")} />
                    </Grid>
                </Grid>
            </Box>
            <Footer {...props} />
        </Box>
    );
}
